import React from 'react';
import { UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { Player, PlayerWithLeagueContext } from '../../../generated-api';
import { Team, TEAM_COLORS } from '../../SortableEntity';
import PlayerAvatar from '../../common/PlayerAvatar';

interface TeamAssignmentStepProps {
  selectedPlayerIds: string[];
  players: (Player | PlayerWithLeagueContext)[];
  teams: Team[];
  onCycleTeam: (playerId: string) => void;
}

const TeamAssignmentStep: React.FC<TeamAssignmentStepProps> = ({
  selectedPlayerIds,
  players,
  teams,
  onCycleTeam,
}) => {
  const getPlayerTeam = (playerId: string) => {
    return teams.find(t => t.playerIds.includes(playerId));
  };

  const getTeamColorEntry = (team: Team | undefined) => {
    if (!team) return null;
    return TEAM_COLORS.find(tc => tc.name.toLowerCase() === team.color) || null;
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="text-center">
        <h2 className="text-lg font-serif font-semibold text-heading">Assign Teams</h2>
        <p className="text-sm text-muted mt-1">Tap each player to group them into teams</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {selectedPlayerIds.map(playerId => {
          const player = players.find(p => p.id === playerId);
          if (!player) return null;

          const team = getPlayerTeam(playerId);
          const colorEntry = getTeamColorEntry(team);

          return (
            <button
              data-track="wizard_team_player"
              key={playerId}
              type="button"
              onClick={() => onCycleTeam(playerId)}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-150
                         active:scale-[0.98] text-left
                         ${colorEntry 
                           ? `${colorEntry.bg} ${colorEntry.border} shadow-sm` 
                           : 'bg-surface border-edge hover:border-primary-300'}`}
            >
              <div className="relative">
                <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="medium" />
                {team && (
                   <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-stone-900 flex items-center justify-center ${colorEntry?.bg.replace('bg-', 'bg-').replace('-100', '-500') || 'bg-primary-500'}`}>
                     <UserGroupIcon className="w-3 h-3 text-white" />
                   </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-heading truncate">{player.name}</div>
                <div className="text-xs mt-0.5">
                  {team ? (
                    <span className={`font-bold uppercase tracking-wider ${colorEntry?.text || 'text-primary-700'}`}>
                      {team.name}
                    </span>
                  ) : (
                    <span className="text-muted flex items-center gap-1">
                      <UserIcon className="w-3 h-3" />
                      Individual
                    </span>
                  )}
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-1.5">
                {TEAM_COLORS.slice(0, 5).map(c => {
                  const isSelected = team?.color === c.name.toLowerCase();
                  return (
                    <div 
                      key={c.name}
                      className={`w-3.5 h-3.5 rounded-full border border-black/10 ${c.bg.replace('-100', '-500')} ${isSelected ? 'ring-2 ring-offset-2 ring-primary-500 scale-125' : 'opacity-40'}`}
                    />
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TeamAssignmentStep;
